import { supabase } from '@/lib/supabase';

export type PaymentProvider = 'chargily' | 'stripe' | 'paypal' | 'manual';
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';
export type PaymentMethod = 'credit_card' | 'debit_card' | 'bank_transfer' | 'cash' | 'ccp' | 'baridi' | 'edahabia';

export interface PaymentIntent {
  id: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  provider: PaymentProvider;
  clientSecret?: string;
  checkoutUrl?: string;
  metadata?: Record<string, any>;
}

export interface PaymentHistoryItem {
  id: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  provider: PaymentProvider;
  method: PaymentMethod;
  description: string;
  createdAt: Date;
  invoiceUrl?: string;
}

export interface ChargilyConfig {
  apiKey: string;
  secretKey: string;
  mode: 'test' | 'live';
}

export interface StripeConfig {
  publishableKey: string;
  secretKey: string;
}

export interface CreatePaymentIntentRequest {
  amount: number;
  currency?: string;
  description: string;
  provider: PaymentProvider;
  metadata?: Record<string, any>;
  successUrl?: string;
  cancelUrl?: string;
}

/**
 * Payment Service - Multi-provider payment processing
 * Supports: Chargily (Algeria), Stripe (International), and manual payments
 */
export class PaymentService {
  private static readonly DEFAULT_CURRENCY = 'DZD';
  private static readonly CENTS_MULTIPLIER = 100;

  /**
   * Create a payment intent for plan upgrade or subscription
   */
  static async createPaymentIntent(
    request: CreatePaymentIntentRequest
  ): Promise<PaymentIntent> {
    const { data, error } = await supabase.functions.invoke('create-payment-intent', {
      body: {
        amount: request.amount,
        currency: request.currency || this.DEFAULT_CURRENCY,
        description: request.description,
        provider: request.provider,
        metadata: request.metadata,
        success_url: request.successUrl,
        cancel_url: request.cancelUrl,
      },
    });

    if (error) {
      throw new Error(`Failed to create payment intent: ${error.message}`);
    }

    return {
      id: data.id,
      amount: data.amount,
      currency: data.currency,
      status: data.status,
      provider: data.provider,
      clientSecret: data.client_secret,
      checkoutUrl: data.checkout_url,
      metadata: data.metadata,
    };
  }

  /**
   * Create a Chargily payment (Algerian payment gateway)
   * Supports: CCP, BaridiMob, EDAHABIA, and card payments
   */
  static async createChargilyPayment(params: {
    amount: number;
    description: string;
    customerEmail: string;
    customerName: string;
    metadata?: Record<string, any>;
    successUrl: string;
    cancelUrl: string;
  }): Promise<PaymentIntent> {
    // Chargily expects amount in cents
    const amountInCents = Math.round(params.amount * this.CENTS_MULTIPLIER);

    const { data, error } = await supabase.functions.invoke('chargily-create-payment', {
      body: {
        amount: amountInCents,
        currency: 'DZD',
        description: params.description,
        customer_email: params.customerEmail,
        customer_name: params.customerName,
        metadata: params.metadata,
        success_url: params.successUrl,
        cancel_url: params.cancelUrl,
        locale: 'fr', // or 'ar' for Arabic
      },
    });

    if (error) {
      throw new Error(`Failed to create Chargily payment: ${error.message}`);
    }

    return {
      id: data.id,
      amount: params.amount,
      currency: 'DZD',
      status: 'pending',
      provider: 'chargily',
      checkoutUrl: data.checkout_url,
      metadata: params.metadata,
    };
  }

  /**
   * Create a Stripe payment intent
   */
  static async createStripePayment(params: {
    amount: number;
    currency?: string;
    description: string;
    customerEmail: string;
    metadata?: Record<string, any>;
  }): Promise<PaymentIntent> {
    // Stripe expects amount in cents
    const amountInCents = Math.round(params.amount * this.CENTS_MULTIPLIER);

    const { data, error } = await supabase.functions.invoke('stripe-create-payment-intent', {
      body: {
        amount: amountInCents,
        currency: params.currency || 'usd',
        description: params.description,
        customer_email: params.customerEmail,
        metadata: params.metadata,
      },
    });

    if (error) {
      throw new Error(`Failed to create Stripe payment: ${error.message}`);
    }

    return {
      id: data.id,
      amount: params.amount,
      currency: params.currency || 'usd',
      status: 'pending',
      provider: 'stripe',
      clientSecret: data.client_secret,
      metadata: params.metadata,
    };
  }

  /**
   * Verify payment status
   */
  static async verifyPayment(
    paymentId: string,
    provider: PaymentProvider
  ): Promise<PaymentIntent> {
    const { data, error } = await supabase.functions.invoke('verify-payment', {
      body: {
        payment_id: paymentId,
        provider,
      },
    });

    if (error) {
      throw new Error(`Failed to verify payment: ${error.message}`);
    }

    return {
      id: data.id,
      amount: data.amount / this.CENTS_MULTIPLIER,
      currency: data.currency,
      status: data.status,
      provider,
      metadata: data.metadata,
    };
  }

  /**
   * Get payment history for a company
   */
  static async getPaymentHistory(companyId: string): Promise<PaymentHistoryItem[]> {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch payment history: ${error.message}`);
    }

    return (data || []).map((item: any) => ({
      id: item.id,
      amount: parseFloat(item.amount),
      currency: item.currency,
      status: item.status,
      provider: item.provider,
      method: item.payment_method,
      description: item.description,
      createdAt: new Date(item.created_at),
      invoiceUrl: item.invoice_url,
    }));
  }

  /**
   * Get payment details by ID
   */
  static async getPaymentById(paymentId: string): Promise<PaymentHistoryItem | null> {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('id', paymentId)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      id: data.id,
      amount: parseFloat(data.amount),
      currency: data.currency,
      status: data.status,
      provider: data.provider,
      method: data.payment_method,
      description: data.description,
      createdAt: new Date(data.created_at),
      invoiceUrl: data.invoice_url,
    };
  }

  /**
   * Request a refund
   */
  static async requestRefund(
    paymentId: string,
    reason: string
  ): Promise<{ success: boolean; refundId?: string }> {
    const { data, error } = await supabase.functions.invoke('request-refund', {
      body: {
        payment_id: paymentId,
        reason,
      },
    });

    if (error) {
      throw new Error(`Failed to request refund: ${error.message}`);
    }

    return {
      success: data.success,
      refundId: data.refund_id,
    };
  }

  /**
   * Get available payment methods for a provider
   */
  static getAvailablePaymentMethods(provider: PaymentProvider): PaymentMethod[] {
    switch (provider) {
      case 'chargily':
        return ['ccp', 'baridi', 'edahabia', 'credit_card'];
      case 'stripe':
        return ['credit_card', 'debit_card'];
      case 'paypal':
        return ['credit_card', 'debit_card'];
      case 'manual':
        return ['cash', 'bank_transfer'];
      default:
        return ['credit_card'];
    }
  }

  /**
   * Get recommended payment provider based on region/currency
   */
  static getRecommendedProvider(currency: string = 'DZD'): PaymentProvider {
    if (currency === 'DZD') {
      return 'chargily'; // Best for Algeria
    }
    return 'stripe'; // Best for international
  }

  /**
   * Format amount for display
   */
  static formatAmount(amount: number, currency: string = 'DZD'): string {
    if (currency === 'DZD') {
      return `${amount.toLocaleString('fr-DZ')} DA`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  }
}

export default PaymentService;
